"use client";

import { useId } from "react";

import { initials, resolveKitPattern, resolveTeamColors, type LogoPattern } from "@/lib/colors";

type Props = {
  name: string;
  short?: string;
  colors?: string[];
  logo?: string;
  pattern?: string;
  size?: number;
  className?: string;
};

const SHIELD = "M20 1 L38 7 V22 C38 33 30 40.5 20 45 C10 40.5 2 33 2 22 V7 Z";

/** Узор формы внутри щита (координаты 40×46). */
function patternNodes(pattern: LogoPattern, second: string) {
  switch (pattern) {
    case "stripes":
      return (
        <>
          <rect x="9" y="0" width="6" height="46" fill={second} />
          <rect x="21" y="0" width="6" height="46" fill={second} />
          <rect x="33" y="0" width="6" height="46" fill={second} />
        </>
      );
    case "pinstripes":
      return (
        <>
          {[6, 12, 18, 24, 30, 36].map((x) => (
            <rect key={x} x={x} y="0" width="1.6" height="46" fill={second} />
          ))}
        </>
      );
    case "hoops":
      return (
        <>
          <rect x="0" y="9" width="40" height="7" fill={second} />
          <rect x="0" y="23" width="40" height="7" fill={second} />
          <rect x="0" y="37" width="40" height="7" fill={second} />
        </>
      );
    case "sash":
      return <path d="M0 36 L40 8 L40 18 L0 46 Z" fill={second} />;
    case "halves":
      return <rect x="20" y="0" width="20" height="46" fill={second} />;
    case "chevron":
      return <path d="M0 14 L20 28 L40 14 L40 23 L20 37 L0 23 Z" fill={second} />;
    case "quarters":
      return (
        <>
          <rect x="20" y="0" width="20" height="23" fill={second} />
          <rect x="0" y="23" width="20" height="23" fill={second} />
        </>
      );
    case "diagonal":
      return (
        <g transform="rotate(-45 20 23)" fill={second}>
          {[-24, -12, 0, 12, 24, 36].map((x) => (
            <rect key={x} x={x} y="-30" width="6" height="110" />
          ))}
        </g>
      );
    case "checker":
      return (
        <g fill={second}>
          {[0, 1, 2, 3].flatMap((col) =>
            [0, 1, 2, 3].map((row) =>
              (col + row) % 2 === 0 ? (
                <rect key={`${col}-${row}`} x={col * 10} y={row * 12} width="10" height="12" />
              ) : null,
            ),
          )}
        </g>
      );
    case "wave":
      return (
        <path
          d="M0 17 Q10 11 20 17 T40 17 V31 Q30 37 20 31 T0 31 Z"
          fill={second}
        />
      );
    case "frame":
      return <rect x="5" y="5" width="30" height="37" fill="none" stroke={second} strokeWidth="5" />;
    default:
      return null;
  }
}

/**
 * Эмблема клуба: загруженный логотип либо щит с монограммой,
 * клубными цветами (до трёх) и узором формы.
 */
export default function TeamCrest({
  name,
  short,
  colors,
  logo,
  pattern,
  size = 28,
  className = "",
}: Props) {
  const uid = useId().replace(/:/g, "");
  const clipId = `clip-${uid}`;
  const shadeId = `shade-${uid}`;

  const palette = resolveTeamColors(name, colors);
  const base = palette[0];
  const second = palette[1] ?? palette[0];
  const trim = palette[2] ?? palette[1] ?? palette[0];
  const kit = resolveKitPattern(name, pattern);
  const monogram = (short || initials(name)).slice(0, 3).toUpperCase();

  // С логотипом щит не рисуем — показываем только загруженную картинку.
  if (logo) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center overflow-hidden ${className}`}
        style={{ width: size, height: (size * 46) / 40 }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={logo}
          alt={`Логотип клуба ${name}`}
          width={size}
          height={size}
          className="h-full w-full object-contain"
        />
      </span>
    );
  }

  return (
    <svg
      width={size}
      height={(size * 46) / 40}
      viewBox="0 0 40 46"
      role="img"
      aria-label={`Эмблема клуба ${name}`}
      className={`shrink-0 ${className}`}
    >
      <defs>
        <linearGradient id={shadeId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.3" />
        </linearGradient>
        <clipPath id={clipId}>
          <path d={SHIELD} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="40" height="46" fill={base} />
        {patternNodes(kit, second)}
        <rect x="0" y="0" width="40" height="46" fill={`url(#${shadeId})`} />
      </g>

      <path d={SHIELD} fill="none" stroke={trim} strokeWidth="2.4" />
      <path d={SHIELD} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.8" />

      <circle cx="20" cy="22" r="9.5" fill="rgba(4,16,12,0.55)" stroke={trim} strokeWidth="1.2" />
      <text
        x="20"
        y="25.6"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize={monogram.length > 2 ? 7.5 : 9.5}
        fontWeight="800"
        letterSpacing="0.3"
        fill="#ffffff"
      >
        {monogram}
      </text>
    </svg>
  );
}
